import type { UserLauncherItemDto, UserLauncherItemPayload } from 'utils/api/dto';
import * as endpoints from 'utils/api/endpoints';

// Header pins of a signed-in user are user launcher items on the backend. The API has no bulk update:
// the items are brought to the pins one request at a time (useLauncherItemsSync).

export type LauncherItemRequest =
    | { method: 'delete'; url: string; item: UserLauncherItemDto }
    | { method: 'post'; url: string; payload: UserLauncherItemPayload }
    // PUT replaces the whole item on the backend (created, label, icon included): the item is sent with its new position
    | { method: 'put'; url: string; payload: UserLauncherItemDto; item: UserLauncherItemDto };

export const sortLauncherItems = (items: unknown): UserLauncherItemDto[] =>
    (Array.isArray(items) ? (items as UserLauncherItemDto[]) : [])
        .filter(item => item?.path)
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

// A standard item: its label and icon come from the link
const toNewItemPayload = (path: string, position: number): UserLauncherItemPayload => ({
    icon: null,
    isCustom: false,
    label: null,
    path,
    position
});

/**
 * The next request that brings the items closer to the pins, null when they match. Removals go first. The positions
 * only have to grow in the order of the pins, not to be 0, 1, 2…: removing a pin or adding one at the end is a
 * single request, and only the items out of order are moved. Each step keeps the pins before it in order, so the
 * requests always come to an end.
 */
export const nextLauncherItemRequest = (
    items: UserLauncherItemDto[],
    pins: readonly string[]
): LauncherItemRequest | null => {
    const pinSet = new Set(pins);
    const removed = items.find(item => !pinSet.has(item.path));
    if (removed) {
        return { method: 'delete', url: endpoints.userLauncherItem(removed.id), item: removed };
    }

    const itemByPath = new Map(items.map(item => [item.path, item]));
    let lastPosition = -1;
    for (const path of pins) {
        const item = itemByPath.get(path);
        if (!item) {
            return {
                method: 'post',
                url: endpoints.userLauncherItems,
                payload: toNewItemPayload(path, lastPosition + 1)
            };
        }
        if (item.position == null || item.position <= lastPosition) {
            return {
                method: 'put',
                url: endpoints.userLauncherItem(item.id),
                payload: { ...item, position: lastPosition + 1 },
                item
            };
        }
        lastPosition = item.position;
    }

    return null;
};

/** The items after a successful request; null when the response does not tell them (reload the items) */
export const applyLauncherItemResponse = (
    items: UserLauncherItemDto[],
    request: LauncherItemRequest,
    response: unknown
): UserLauncherItemDto[] | null => {
    if (request.method === 'delete') {
        return items.filter(item => item.id !== request.item.id);
    }

    if (request.method === 'put') {
        return items.map(item => (item.id === request.item.id ? { ...item, ...request.payload } : item));
    }

    const created = response as UserLauncherItemDto | null;

    return created?.id ? [...items, created] : null;
};
